import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export interface WorkCost {
  id: number;
  workId: number;
  workCostTypeId: number;
  typeCode: string;
  typeName: string;
  description: string;
  costDate: string;
  amount: number;
  supplier?: string | null;
  documentReference?: string | null;
  workerId?: number | null;
  workerName?: string | null;
  hours?: number | null;
  hourlyRate?: number | null;
  quantity?: number | null;
  unit?: string | null;
  notes?: string | null;
  budgetAreaName?: string | null;
  budgetChapterName?: string | null;
  created: string;
  lastModified: string;
}

export interface WorkCostPayload {
  workId: number;
  workCostTypeId: number;
  description: string;
  costDate?: string | null;
  amount: number;
  supplier?: string | null;
  documentReference?: string | null;
  workerId?: number | null;
  hours?: number | null;
  hourlyRate?: number | null;
  quantity?: number | null;
  unit?: string | null;
  notes?: string | null;
  budgetAreaName?: string | null;
  budgetChapterName?: string | null;
}

export interface WorkCostType {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  return (await response.json()) as T;
}

// [4.4.5] Rateio de custos indiretos pelas obras ativas.
export interface IndirectAllocationPayload {
  criterion?: string | null;
  totalAmount: number;
  description: string;
  costDate: string;
  confirm: boolean;
}

export interface IndirectAllocationLine {
  workId: number;
  workName: string;
  weight: number;
  weightPercent: number;
  amount: number;
}

export interface IndirectAllocationResult {
  criterion: string;
  totalAmount: number;
  confirmed: boolean;
  equalFallback: boolean;
  includedCount: number;
  allocations: IndirectAllocationLine[];
}

export function allocateIndirectCost(
  payload: IndirectAllocationPayload,
): Promise<IndirectAllocationResult> {
  return request<IndirectAllocationResult>("/api/workcosts/indirect-allocation", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function getWorkCosts(workId?: number): Promise<WorkCost[]> {
  const query =
    workId !== undefined && workId !== null && Number.isFinite(workId)
      ? `?workId=${workId}`
      : "";
  return request<WorkCost[]>(`/api/workcosts${query}`);
}

export function getWorkCostById(costId: number): Promise<WorkCost> {
  return request<WorkCost>(`/api/workcosts/${costId}`);
}

export function getWorkCostTypes(): Promise<WorkCostType[]> {
  return request<WorkCostType[]>("/api/workcosts/types");
}

export function createWorkCost(payload: WorkCostPayload): Promise<WorkCost> {
  return request<WorkCost>("/api/workcosts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateWorkCost(
  costId: number,
  payload: WorkCostPayload & { id: number },
): Promise<WorkCost> {
  return request<WorkCost>(`/api/workcosts/${costId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function deleteWorkCost(costId: number): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/workcosts/${costId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
